// @flow
import React from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import {
  Icon,
  Text,
  HighlightKeyword,
  ConfirmationModal,
  EllipsisText,
} from 'bench-internal-theme';
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { openModal } from 'modal-core-components';
import formatDaySuffix from '../formatDaySuffix';
import LedgerAccountSidebar from '../ledger-account-sidebar/LedgerAccountSidebar';
// $FlowFixMe - TS file
import DisplayGroupCell from './DisplayGroupCell';
import type { LedgerAccount, LedgerCategory } from '../types';
import volumeLabels from '../volumeLabels';
import { Tooltip } from 'client-web-app/ui';
import TooltipContentPre from '../TooltipContentPre';
import { useDrag } from 'react-dnd';
import { useMediaQuery } from 'react-responsive';
import breakpoints from '../../constants/breakpoints';

const IconWrapper = styled.div`
  position: center;
  text-align: center;
`;

const LedgerAccountsTableRowLayout = styled.div`
  align-items: center;
  background-color: ${({ isDragging }) => (isDragging ? colors.sand : colors.white)};
  border-bottom: 1px solid ${colors.silt};
  box-shadow: 0 -1px 0 ${colors.silt};
  display: grid;
  grid-column-gap: 16px;
  margin: 0 32px;
  min-height: 41px;
  opacity: ${({ isDragging }) => (isDragging ? '.5' : '1')};
  padding: 8px;
  position: relative;
  cursor: ${({ isDragging, isSorting, canSort }) => {
    if (isSorting && canSort) {
      return isDragging ? 'grabbing' : 'grab';
    }
    return 'default';
  }};
  opacity: ${({ isSortSaving }) => (isSortSaving ? 0.5 : 1)};
  transition: ${({ isSortSaving }) => (isSortSaving ? 0.5 : 1)};
  grid-template-columns: ${({ isTablet, isLd, isHd, isAssignCategoryImprovementEnabled }) => {
    if (isHd) {
      return isAssignCategoryImprovementEnabled
        ? `auto 240px 16px 64px 112px 112px 112px 112px 16px 16px`
        : `auto 16px 64px 112px 112px 112px 112px 112px 112px 16px 16px`;
    }
    if (isLd) {
      return isAssignCategoryImprovementEnabled
        ? `auto 240px 16px 64px 112px 112px 112px 16px 16px`
        : `auto 16px 64px 112px 112px 112px 112px 16px 16px`;
    }
    if (isTablet) {
      return isAssignCategoryImprovementEnabled
        ? `auto 240px 16px 64px 16px 16px`
        : `auto 16px 64px 112px 16px 16px`;
    }
    return `auto 16px 16px`;
  }};
`;

type LedgerAccountsTableRowProps = {
  ledgerAccount: LedgerAccount,
  search: string,
  deleteLedgerAccount: () => any,
  ledgerCategory: LedgerCategory,
  isSorting: boolean,
  isSortSaving: boolean,
  canSort: boolean,
  isAssignCategoryImprovementEnabled: boolean,
  onDisplayGroupSaveStart: () => void,
  onDisplayGroupSaveComplete: () => void,
};

const LedgerAccountsTableRow = ({
  deleteLedgerAccount,
  ledgerAccount,
  search,
  ledgerCategory,
  isSorting,
  isSortSaving,
  canSort,
  isAssignCategoryImprovementEnabled,
  onDisplayGroupSaveStart,
  onDisplayGroupSaveComplete,
}: LedgerAccountsTableRowProps) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: `${ledgerCategory.name}-row`,
      item: { id: ledgerAccount.id },
      canDrag: () => isSorting && canSort,
      collect: monitor => ({
        isDragging: !!monitor.isDragging(),
        canDrag: !!monitor.canDrag(),
      }),
    }),
    [isSorting, canSort],
  );

  const { role, accountSettings, name, statementDayOfMonth, details, isAutoGenerated } =
    ledgerAccount;

  const handleDelete = () => {
    openModal(closeModal => (
      <ConfirmationModal
        closeModal={closeModal}
        description={`Would you like to delete ${name}? This action is irreversible.`}
        onConfirm={deleteLedgerAccount}
        title="Are you sure?"
      />
    ));
  };
  const isHd = useMediaQuery({ minWidth: breakpoints.hd });
  const isLd = useMediaQuery({ minWidth: breakpoints.ld });
  const isTablet = useMediaQuery({ minWidth: breakpoints.tablet });

  return (
    <LedgerAccountsTableRowLayout
      ref={drag}
      isDragging={isDragging}
      data-testid="ledger-account-table-row"
      isSorting={isSorting}
      isSortSaving={isSortSaving}
      canSort={canSort}
      isTablet={isTablet}
      isLd={isLd}
      isHd={isHd}
      isAssignCategoryImprovementEnabled={isAssignCategoryImprovementEnabled}
    >
      <EllipsisText size="medium" color={colors.charcoal}>
        <HighlightKeyword keyword={search}>{name}</HighlightKeyword>
        <span style={{ color: '#098B8C' }}>{isAutoGenerated ? ' (System Generated)' : null}</span>
      </EllipsisText>
      {isTablet && isAssignCategoryImprovementEnabled && (
        <DisplayGroupCell
          ledgerAccount={ledgerAccount}
          onSaveStart={onDisplayGroupSaveStart}
          onSaveComplete={onDisplayGroupSaveComplete}
          isSortSaving={isSortSaving}
        />
      )}
      {isTablet && (
        <>
          <IconWrapper>
            {details && (
              <Tooltip
                inlineFlex={false}
                placement="bottom"
                tooltipContent={<TooltipContentPre>{details}</TooltipContentPre>}
                styleType="light"
              >
                <Icon
                  cursor="help"
                  title="Notes"
                  hoverColor={colors.silt}
                  color={colors.dust}
                  icon="notebook"
                  size="small"
                />
              </Tooltip>
            )}
          </IconWrapper>
          <IconWrapper>
            {role &&
              (ledgerCategory.id === '9' ? (
                <Text>P/B</Text>
              ) : (
                <Icon
                  title="Ledger is account"
                  color={colors.dust}
                  icon="circleCheck"
                  size="small"
                />
              ))}
          </IconWrapper>
          {!isAssignCategoryImprovementEnabled && (
            <Text styleType="small" color={colors.cloud}>
              {statementDayOfMonth && formatDaySuffix(statementDayOfMonth)}
            </Text>
          )}
        </>
      )}
      {isLd && (
        <>
          <Text styleType="small" color={colors.cloud}>
            {accountSettings && accountSettings.dateActive}
          </Text>
          <Text styleType="small" color={colors.cloud}>
            {accountSettings && accountSettings.dateInactive}
          </Text>
          {!isAssignCategoryImprovementEnabled && (
            <Text styleType="small" color={colors.cloud}>
              {accountSettings && accountSettings.volume && volumeLabels[accountSettings.volume]}
            </Text>
          )}
        </>
      )}
      {isHd && (
        <>
          <Text styleType="small" color={colors.cloud}>
            {accountSettings && accountSettings.collectionMethod}
          </Text>
          <Text styleType="small" color={colors.cloud}>
            {accountSettings && accountSettings.optimizationStatus}
          </Text>
        </>
      )}
      <AppSidebar>
        {({ closeSidebar, openSidebar }) => (
          <Icon
            cursor="pointer"
            color={colors.cloud}
            hoverColor={colors.silt}
            onClick={() =>
              openSidebar(
                <LedgerAccountSidebar id={ledgerAccount.id} closeSidebar={closeSidebar} />,
              )
            }
            icon="pencil"
            size="small"
            aria-label="Edit Ledger Account"
            title="Edit Ledger Account"
          />
        )}
      </AppSidebar>
      <Icon
        title="Delete Ledger Account"
        cursor="pointer"
        color={colors.cloud}
        hoverColor={colors.silt}
        icon="close"
        onClick={handleDelete}
        size="small"
        aria-label="Delete Ledger Account"
      />
    </LedgerAccountsTableRowLayout>
  );
};

export default LedgerAccountsTableRow;
